import { z, type ZodIssue } from "zod";

type ErrorMapCtx = {
  defaultError: string;
  data: unknown;
};

const ltMessages = {
  errors: {
    invalid_type: "Tikėtasi {{expected}}, gauta(s) {{received}}",
    invalid_type_received_undefined: "Šis laukas yra privalomas",
    invalid_type_received_null: "Šis laukas yra privalomas",
    invalid_literal: "Klaidinga literalo reikšmė, tikėtasi {{expected}}",
    unrecognized_keys: "Neatpažintas(i) raktas(ai) objekte: {{- keys}}",
    invalid_union: "Klaidinga įvestis",
    invalid_union_discriminator: "Netinkama diksriminanto reikšmė. Tikėtasi {{- options}}",
    invalid_enum_value: "Klaidinga reikšmė. Tikėtasi {{- options}}, gauta '{{received}}'",
    invalid_arguments: "Klaidingi funkcijos argumentai",
    invalid_return_type: "Klaidingas funkcijos gražinamos reikšmės tipas",
    invalid_date: "Klaidinga data",
    custom: "Klaidinga įvestis",
    invalid_intersection_types: "Negalima sujungti sankirtos rezultatų",
    not_multiple_of: "Skaičius privalo būti {{multipleOf}} kartotinis",
    not_finite: "Skaičius privalo būti baigtinis",
    invalid_string: {
      email: "Klaidingas {{validation}}",
      url: "Klaidinga {{validation}}",
      uuid: "Klaidingas {{validation}}",
      cuid: "Klaidingas {{validation}}",
      regex: "Klaidingas {{validation}}",
      datetime: "Klaidinga {{validation}}",
      startsWith: 'Klaidinga įvestis: privaloma pradžia "{{startsWith}}"',
      endsWith: 'Klaidinga įvestis: privaloma pabaiga "{{endsWith}}"',
    },
    too_small: {
      array: {
        exact: "Masyvas privalo turėti {{minimum}} elementą(us)",
        inclusive: "Masyvas privalo turėti bent {{minimum}} elementą(us)",
        not_inclusive: "Masyvas privalo turėti daugiau, nei {{minimum}} elementą(us)",
      },
      string: {
        exact: "Tekstas privalo būti sudarytas iš {{minimum}} simbolio(ių)",
        inclusive: "Tekstas privalo būti sudarytas, bent iš {{minimum}} simbolio(ių)",
        not_inclusive: "Tekstas privalo būti ilgesnis, nei {{minimum}} simbolis(iai)",
      },
      number: {
        exact: "Skaičius privalo būti {{minimum}}",
        inclusive: "Skaičius privalo būti didesnis arba lygus {{minimum}}",
        not_inclusive: "Skaičius privalo būti didesnis, nei {{minimum}}",
      },
      set: {
        exact: "Klaidinga įvestis",
        inclusive: "Klaidinga įvestis",
        not_inclusive: "Klaidinga įvestis",
      },
      date: {
        exact: "Data privalo būti {{- minimum, datetime}}",
        inclusive: "Data privalo būti vėlesnė arba lygi {{- minimum, datetime}}",
        not_inclusive: "Data privalo būti vėlesnė, nei {{- minimum, datetime}}",
      },
    },
    too_big: {
      array: {
        exact: "Masyvas privalo turėti {{maximum}} elementą(us)",
        inclusive: "Masyvas negali turėti daugiau, nei {{maximum}} elementą(us)",
        not_inclusive: "Masyvas privalo turėti mažiau, nei {{maximum}} elementą(us)",
      },
      string: {
        exact: "Tekstas privalo būti sudarytas iš {{maximum}} simbolio(ių)",
        inclusive: "Tekstas negali būti sudarytas iš daugiau, nei {{maximum}} simbolio(ių)",
        not_inclusive: "Tekstas privalo būti trumpesnis, nei {{maximum}} simbolis(iai)",
      },
      number: {
        exact: "Skaičius privalo būti {{maximum}}",
        inclusive: "Skaičius privalo būti mažesnis arba lygus {{maximum}}",
        not_inclusive: "Skaičius privalo būti mažesnis, nei {{maximum}}",
      },
      set: {
        exact: "Klaidinga įvestis",
        inclusive: "Klaidinga įvestis",
        not_inclusive: "Klaidinga įvestis",
      },
      date: {
        exact: "Data privalo būti {{- maximum, datetime}}",
        inclusive: "Data privalo būti ankstesnė arba lygi {{- maximum, datetime}}",
        not_inclusive: "Data privalo būti ankstesnė, nei {{- maximum, datetime}}",
      },
    },
  },
  validations: {
    email: "el. paštas",
    url: "nuoroda",
    uuid: "uuid",
    cuid: "cuid",
    regex: "reguliarus išraiškos šablonas",
    datetime: "datos ir laiko reikšmė",
  },
  types: {
    function: "funkcija",
    number: "skaičius",
    string: "tekstas",
    nan: "nan",
    integer: "sveikasis skaičius",
    float: "slankusis kablelis",
    boolean: "loginė reikšmė",
    date: "data",
    bigint: "didelis sveikasis skaičius",
    undefined: "neapibrėžtas",
    symbol: "simbolis",
    null: "null",
    array: "masyvas",
    object: "objektas",
    unknown: "nežinomas",
    promise: "pažadas",
    void: "tuščias",
    never: "niekada",
    map: "rakto-reikšmių sąrašas",
    set: "rinkinys",
  },
};

const customErrorMap = (issue: ZodIssue, _ctx: ErrorMapCtx) => {
  // Handle special cases for invalid_type with undefined/null
  if (issue.code === "invalid_type") {
    if ("received" in issue && issue.received === "undefined") {
      return { message: ltMessages.errors.invalid_type_received_undefined };
    }
    if ("received" in issue && issue.received === "null") {
      return { message: ltMessages.errors.invalid_type_received_null };
    }
  }

  const message = ltMessages.errors[issue.code as keyof typeof ltMessages.errors];

  if (typeof message === "string") {
    let processedMessage = message;

    // Replace type placeholders
    if ("expected" in issue && typeof issue.expected === "string") {
      const typeTranslation =
        ltMessages.types[issue.expected as keyof typeof ltMessages.types] || issue.expected;
      processedMessage = processedMessage.replace(/{{expected}}/g, typeTranslation);
    }
    if ("received" in issue && typeof issue.received === "string") {
      const typeTranslation =
        ltMessages.types[issue.received as keyof typeof ltMessages.types] || issue.received;
      processedMessage = processedMessage.replace(/{{received}}/g, typeTranslation);
    }

    // Handle properties that may exist on specific issue types
    if ("keys" in issue && Array.isArray(issue.keys)) {
      processedMessage = processedMessage.replace(/{{- keys}}/g, issue.keys.join(", "));
    }
    if ("options" in issue && Array.isArray(issue.options)) {
      processedMessage = processedMessage.replace(/{{- options}}/g, issue.options.join(", "));
    }
    if ("minimum" in issue && typeof issue.minimum === "number") {
      processedMessage = processedMessage.replace(/{{minimum}}/g, issue.minimum.toString());
      processedMessage = processedMessage.replace(
        /{{- minimum, datetime}}/g,
        new Date(issue.minimum).toISOString(),
      );
    }
    if ("maximum" in issue && typeof issue.maximum === "number") {
      processedMessage = processedMessage.replace(/{{maximum}}/g, issue.maximum.toString());
      processedMessage = processedMessage.replace(
        /{{- maximum, datetime}}/g,
        new Date(issue.maximum).toISOString(),
      );
    }
    if ("multipleOf" in issue && typeof issue.multipleOf === "number") {
      processedMessage = processedMessage.replace(/{{multipleOf}}/g, issue.multipleOf.toString());
    }
    if ("startsWith" in issue && typeof issue.startsWith === "string") {
      processedMessage = processedMessage.replace(/{{startsWith}}/g, issue.startsWith);
    }
    if ("endsWith" in issue && typeof issue.endsWith === "string") {
      processedMessage = processedMessage.replace(/{{endsWith}}/g, issue.endsWith);
    }

    return { message: processedMessage };
  }

  if (typeof message === "object" && message !== null) {
    // Handle nested message objects (like invalid_string)
    if ("validation" in issue && typeof issue.validation === "string") {
      const validation =
        ltMessages.validations[issue.validation as keyof typeof ltMessages.validations];
      if (validation && typeof message === "object" && validation in message) {
        const validationMessage = (message as Record<string, string>)[validation];
        if (typeof validationMessage === "string") {
          return {
            message: validationMessage.replace(/{{validation}}/g, validation),
          };
        }
      }
    }

    // Handle too_small and too_big nested structures
    if (issue.code === "too_small" || issue.code === "too_big") {
      const typeKey = (
        "type" in issue && typeof issue.type === "string" ? issue.type : "string"
      ) as keyof typeof message;
      const inclusiveKey = (
        "inclusive" in issue && issue.inclusive ? "inclusive" : "not_inclusive"
      ) as "inclusive" | "not_inclusive" | "exact";

      // message is already the too_small or too_big object, not the errors object
      if (typeof message === "object" && message !== null && typeKey in message) {
        const typeMessages = (message as Record<string, Record<string, string>>)[typeKey];
        if (typeMessages && inclusiveKey in typeMessages) {
          let processedMessage = typeMessages[inclusiveKey];
          if ("minimum" in issue && typeof issue.minimum === "number") {
            processedMessage = processedMessage.replace(/{{minimum}}/g, issue.minimum.toString());
            processedMessage = processedMessage.replace(
              /{{- minimum, datetime}}/g,
              new Date(issue.minimum).toISOString(),
            );
          }
          if ("maximum" in issue && typeof issue.maximum === "number") {
            processedMessage = processedMessage.replace(/{{maximum}}/g, issue.maximum.toString());
            processedMessage = processedMessage.replace(
              /{{- maximum, datetime}}/g,
              new Date(issue.maximum).toISOString(),
            );
          }
          return { message: processedMessage };
        }
      }
    }
  }

  // Always return Lithuanian message, never use default English error
  return { message: "Klaidinga įvestis" };
};

// Note: Using 'as any' because TypeScript definitions for z.config don't include 'errorMap' yet,
// even though the runtime supports it (as indicated by the deprecation of z.setErrorMap)
z.config({ errorMap: customErrorMap } as any);

// export configured zod instance
export { z };
